import { BAOCustomElement } from '@bao/custom-element'
import { translateString } from '@bao/utils'

const SAVED_AUTO_CLOSE_DELAY = 5000
const ADDED_AUTO_CLOSE_DELAY = 2500
const VIEW_EXIT_DURATION = 100
const VIEW_MORPH_DURATION = 250

const VIEWS = {
  SAVED: 'saved',
  LISTS: 'lists',
  CREATE: 'create',
  ADDED: 'added',
}

const EVENTS = {
  ADD_TO_LIST: 'wishlist-toast:add-to-list',
  CREATE_LIST: 'wishlist-toast:create-list',
  SAVE_LISTS: 'wishlist-toast:save-lists',
  SAVE_CREATE: 'wishlist-toast:save-create',
  VIEW: 'wishlist-toast:view',
  RETURN_TO_WISHLIST: 'wishlist-toast:return-to-wishlist',
  COMPLETE: 'wishlist-toast:complete',
}

class WishlistToast extends BAOCustomElement() {
  static get requiredElements () {
    return [
      'container',
      'inner',
      'savedView',
      'listsView',
      'createView',
      'addedView',
      'close',
      'imageContainer',
      'image',
      'status',
      'productTitle',
      'lists',
      'listTemplate',
      'listsForm',
      'listsSave',
      'createForm',
      'createInput',
      'error',
    ]
  }

  constructor () {
    super()

    this.activeView = VIEWS.SAVED
    this.currentItemId = null
    this.lastLists = []
    this.lastSelectedListIds = []
    this.addedLists = []

    this.completeTimeout = null
    this.autoCloseStartedAt = null
    this.autoCloseRemaining = 0

    this.productImageRequestId = 0
    this.viewTransitionId = 0
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(this, 'click', this.handleClick.bind(this))
    this.listeners.add(this, 'pointerenter', this.pauseAutoClose.bind(this))
    this.listeners.add(this, 'pointerleave', this.resumeAutoClose.bind(this))
    this.listeners.add(this, 'focusin', this.pauseAutoClose.bind(this))
    this.listeners.add(this, 'focusout', this.handleFocusOut.bind(this))
    this.listeners.add(
      this.els.listsForm.element,
      'submit',
      this.handleListsSubmit.bind(this),
    )
    this.listeners.add(
      this.els.listsForm.element,
      'change',
      this.handleListsChange.bind(this),
    )
    this.listeners.add(
      this.els.createForm.element,
      'submit',
      this.handleCreateSubmit.bind(this),
    )
  }

  disconnectedCallback () {
    this.resetAutoClose()
    this.cancelViewTransition()

    super.disconnectedCallback()
  }

  /*
  |--------------------------------------------------------------------------
  | Saved
  |--------------------------------------------------------------------------
  |
  */
  showSaved ({ itemId, title, image }) {
    this.standaloneCreate = false
    this.els.container.element.removeAttribute('data-standalone-create')
    this.currentItemId = itemId
    this.lastLists = []
    this.lastSelectedListIds = []
    this.addedLists = []

    this.els.status.element.textContent = this.strings.toastSaved

    this.renderProduct({
      title,
      image,
    })

    this.clearError()

    this.showView(VIEWS.SAVED).then(() => {
      if (this.activeView !== VIEWS.SAVED) return

      this.startAutoClose(SAVED_AUTO_CLOSE_DELAY)
    })
  }

  /*
  |--------------------------------------------------------------------------
  | Lists
  |--------------------------------------------------------------------------
  |
  */
  showLists ({ lists, selectedListIds = [] }) {
    this.resetAutoClose()

    this.lastLists = lists
    this.lastSelectedListIds = selectedListIds

    this.clearError()
    this.renderLists(lists, selectedListIds)
    this.showView(VIEWS.LISTS)
  }

  renderLists (lists, selectedListIds) {
    const fragment = document.createDocumentFragment()

    lists.forEach((list) => {
      const item =
        this.els.listTemplate.element.content.firstElementChild.cloneNode(true)

      const checkbox = item.querySelector(
        '[data-wishlist-toast-list-el="checkbox"]',
      )
      const image = item.querySelector('[data-wishlist-toast-list-el="image"]')
      const name = item.querySelector('[data-wishlist-toast-list-el="name"]')
      const previewImage = list.previewImages?.[0]
      const isExisting = selectedListIds.includes(list.id)

      checkbox.value = list.id
      checkbox.checked = isExisting
      checkbox.disabled = isExisting
      checkbox.dataset.listName = list.name
      checkbox.dataset.listCount = String(list.itemCount || 0)

      name.textContent = list.name

      if (previewImage?.url) {
        image.src = previewImage.url
        image.alt = previewImage.altText || ''
        image.hidden = false
      }

      fragment.append(item)
    })

    this.els.lists.element.replaceChildren(fragment)

    this.updateListsSaveButton()
  }

  handleListsChange () {
    this.clearError()
    this.updateListsSaveButton()
  }

  handleListsSubmit (event) {
    event.preventDefault()

    const selectedLists = Array.from(
      this.els.lists.element.querySelectorAll(
        '[data-wishlist-toast-list-el="checkbox"]:checked:not(:disabled)',
      ),
    ).map((checkbox) => {
      return {
        id: checkbox.value,
        name: checkbox.dataset.listName,
        itemCount: Number(checkbox.dataset.listCount || 0),
      }
    })

    if (selectedLists.length === 0) {
      this.showError(this.strings.toastSelectList)

      return
    }

    this.dispatch(EVENTS.SAVE_LISTS, {
      itemId: this.currentItemId,
      lists: selectedLists,
    })
  }

  updateListsSaveButton () {
    const hasSelection = Boolean(
      this.els.lists.element.querySelector(
        '[data-wishlist-toast-list-el="checkbox"]:checked:not(:disabled)',
      ),
    )

    this.els.listsSave.element.disabled = !hasSelection
  }

  /*
  |--------------------------------------------------------------------------
  | Create list
  |--------------------------------------------------------------------------
  |
  */
  showCreate ({ standalone = false } = {}) {
    this.resetAutoClose()
    this.clearError()

    this.standaloneCreate = standalone
    this.els.container.element.toggleAttribute(
      'data-standalone-create',
      standalone,
    )

    if (standalone) {
      this.currentItemId = null
      this.lastLists = []
      this.lastSelectedListIds = []
    }

    this.els.createInput.element.value = ''

    this.showView(VIEWS.CREATE).then(() => {
      if (this.activeView !== VIEWS.CREATE) return

      this.els.createInput.element.focus()
    })
  }

  handleCreateSubmit (event) {
    event.preventDefault()

    const name = this.els.createInput.element.value.trim()

    if (!name) {
      this.showError(this.strings.toastListNameRequired)

      return
    }

    this.dispatch(EVENTS.SAVE_CREATE, {
      itemId: this.currentItemId,
      name,
    })
  }

  /*
  |--------------------------------------------------------------------------
  | Added
  |--------------------------------------------------------------------------
  |
  */
  showAdded ({ lists }) {
    const listNames = lists.map((list) => list.name)
    const translation =
      listNames.length === 1
        ? this.strings.toastAddedToList
        : this.strings.toastAddedToLists

    const variables =
      listNames.length === 1
        ? { list: listNames[0] }
        : { count: listNames.length }

    this.addedLists = lists
    this.els.status.element.textContent = translateString(
      translation,
      variables,
    )

    this.clearError()

    this.showView(VIEWS.ADDED).then(() => {
      if (this.activeView !== VIEWS.ADDED) return

      this.startAutoClose(ADDED_AUTO_CLOSE_DELAY)
    })
  }

  /*
  |--------------------------------------------------------------------------
  | Actions
  |--------------------------------------------------------------------------
  |
  */
  handleClick (event) {
    const trigger = event.target.closest('[data-wishlist-toast-action]')

    if (!trigger) return

    const action = trigger.dataset.wishlistToastAction

    if (action === 'add-to-list') {
      this.resetAutoClose()

      this.dispatch(EVENTS.ADD_TO_LIST, {
        itemId: this.currentItemId,
      })

      return
    }

    if (action === 'create-list') {
      this.dispatch(EVENTS.CREATE_LIST, {
        itemId: this.currentItemId,
      })

      return
    }

    if (action === 'view') {
      this.resetAutoClose()

      this.dispatch(EVENTS.VIEW, {
        lists: this.addedLists,
      })

      return
    }

    if (action === 'back') {
      this.handleBack()
    }
  }

  handleBack () {
    if (this.standaloneCreate) {
      this.dispatch(EVENTS.RETURN_TO_WISHLIST)

      return
    }

    if (this.activeView === VIEWS.CREATE && this.lastLists.length > 0) {
      this.showLists({
        lists: this.lastLists,
        selectedListIds: this.lastSelectedListIds,
      })

      return
    }

    this.showView(VIEWS.SAVED).then(() => {
      if (this.activeView !== VIEWS.SAVED) return

      this.startAutoClose(SAVED_AUTO_CLOSE_DELAY)
    })
  }

  dispatch (name, detail = {}) {
    this.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        detail,
      }),
    )
  }

  /*
  |--------------------------------------------------------------------------
  | Product
  |--------------------------------------------------------------------------
  |
  */
  renderProduct ({ title, image }) {
    this.els.productTitle.element.textContent = title

    this.renderProductImage(image)
  }

  renderProductImage (src) {
    const image = this.els.image.element
    const imageContainer = this.els.imageContainer.element
    const requestId = ++this.productImageRequestId

    image.removeAttribute('data-loaded')
    image.removeAttribute('src')

    imageContainer.toggleAttribute('data-loading', Boolean(src))

    if (!src) return

    const cleanup = () => {
      image.removeEventListener('load', handleLoad)
      image.removeEventListener('error', handleError)
    }

    const handleLoad = () => {
      cleanup()

      if (requestId !== this.productImageRequestId) return

      image.setAttribute('data-loaded', '')
      imageContainer.removeAttribute('data-loading')
    }

    const handleError = () => {
      cleanup()

      if (requestId !== this.productImageRequestId) return

      imageContainer.removeAttribute('data-loading')
    }

    image.addEventListener('load', handleLoad)
    image.addEventListener('error', handleError)

    image.src = src

    if (image.complete) {
      requestAnimationFrame(() => {
        if (image.naturalWidth > 0) {
          handleLoad()
        } else {
          handleError()
        }
      })
    }
  }

  /*
  |--------------------------------------------------------------------------
  | Views
  |--------------------------------------------------------------------------
  |
  */
  async showView (view) {
    if (!this.views[view]) return

    const previousView = this.activeView

    this.cancelViewTransition()
    this.setViewVisibility(previousView)

    this.activeView = view
    this.dataset.view = view
    this.updateCloseButton(view)

    if (
      previousView === view ||
      this.prefersReducedMotion ||
      !this.isConnected
    ) {
      this.setViewVisibility(view)

      return
    }

    const transitionId = this.viewTransitionId
    const inner = this.els.inner.element
    const currentView = this.views[previousView]
    const nextView = this.views[view]
    const startHeight = inner.offsetHeight

    inner.style.blockSize = `${startHeight}px`
    inner.setAttribute('data-morphing', '')

    currentView.setAttribute('data-view-transition', 'leaving')

    await this.wait(VIEW_EXIT_DURATION)

    if (transitionId !== this.viewTransitionId) return

    currentView.hidden = true
    currentView.removeAttribute('data-view-transition')

    nextView.hidden = false
    nextView.setAttribute('data-view-transition', 'entering')

    inner.style.blockSize = 'auto'

    const endHeight = inner.offsetHeight

    inner.style.blockSize = `${startHeight}px`
    inner.getBoundingClientRect()

    await this.nextFrame()

    if (transitionId !== this.viewTransitionId) return

    inner.style.blockSize = `${endHeight}px`
    nextView.removeAttribute('data-view-transition')

    await this.wait(VIEW_MORPH_DURATION)

    if (transitionId !== this.viewTransitionId) return

    this.finishViewTransition()
  }

  updateCloseButton (view) {
    this.els.close.element.hidden = ![VIEWS.LISTS, VIEWS.CREATE].includes(view)
  }

  setViewVisibility (view) {
    Object.entries(this.views).forEach(([viewName, element]) => {
      element.hidden = viewName !== view
      element.removeAttribute('data-view-transition')
    })
  }

  cancelViewTransition () {
    this.viewTransitionId += 1
    this.finishViewTransition()
  }

  finishViewTransition () {
    const inner = this.els.inner?.element

    if (inner) {
      inner.removeAttribute('data-morphing')
      inner.style.removeProperty('block-size')
    }

    Object.values(this.views).forEach((element) => {
      element.removeAttribute('data-view-transition')
    })
  }

  nextFrame () {
    return new Promise((resolve) => {
      requestAnimationFrame(resolve)
    })
  }

  wait (duration) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, duration)
    })
  }

  get views () {
    return {
      [VIEWS.SAVED]: this.els.savedView.element,
      [VIEWS.LISTS]: this.els.listsView.element,
      [VIEWS.CREATE]: this.els.createView.element,
      [VIEWS.ADDED]: this.els.addedView.element,
    }
  }

  get prefersReducedMotion () {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }

  /*
  |--------------------------------------------------------------------------
  | State
  |--------------------------------------------------------------------------
  |
  */
  setLoading (isLoading) {
    this.setAttribute('aria-busy', String(isLoading))
    this.els.container.element.toggleAttribute('data-loading', isLoading)
  }

  startAutoClose (delay) {
    this.resetAutoClose()

    this.autoCloseRemaining = delay
    this.resumeAutoClose()
  }

  handleFocusOut () {
    requestAnimationFrame(() => {
      this.resumeAutoClose()
    })
  }

  pauseAutoClose () {
    if (!this.completeTimeout || this.autoCloseStartedAt === null) return

    const elapsed = performance.now() - this.autoCloseStartedAt

    window.clearTimeout(this.completeTimeout)

    this.completeTimeout = null
    this.autoCloseStartedAt = null
    this.autoCloseRemaining = Math.max(0, this.autoCloseRemaining - elapsed)
  }

  resumeAutoClose () {
    if (this.completeTimeout) return
    if (this.matches(':hover')) return
    if (this.contains(document.activeElement)) return
    if (![VIEWS.SAVED, VIEWS.ADDED].includes(this.activeView)) return
    if (this.autoCloseRemaining <= 0) return

    this.autoCloseStartedAt = performance.now()

    this.completeTimeout = window.setTimeout(() => {
      this.completeTimeout = null
      this.autoCloseStartedAt = null
      this.autoCloseRemaining = 0

      this.dispatch(EVENTS.COMPLETE)
    }, this.autoCloseRemaining)
  }

  resetAutoClose () {
    if (this.completeTimeout) {
      window.clearTimeout(this.completeTimeout)
    }

    this.completeTimeout = null
    this.autoCloseStartedAt = null
    this.autoCloseRemaining = 0
  }

  /*
  |--------------------------------------------------------------------------
  | Errors
  |--------------------------------------------------------------------------
  |
  */
  showError (message) {
    this.els.error.element.textContent = message
    this.els.error.element.hidden = false
  }

  clearError () {
    this.els.error.element.textContent = ''
    this.els.error.element.hidden = true
  }

  get strings () {
    return window.theme.strings.wishlist
  }
}

if (!customElements.get('wishlist-toast')) {
  customElements.define('wishlist-toast', WishlistToast)
}
